import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  Menu, X, RadioTower, Timer, Activity,
  Monitor, Globe, Zap, Coffee, LogOut,
  PhoneCall, Clock, ChevronRight
} from 'lucide-react';
import { useAgentContext } from '../../../../app/modules/apps/call/AgentContext';
import { useThemeMode } from '../../../partials/layout/theme-mode/ThemeModeProvider';
import { toAbsoluteUrl } from '../../../helpers';
import { NavbarSoftphone } from './NavbarSoftphone';

export const MobileAgentBar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showQueueDetails, setShowQueueDetails] = useState(false);

  const {
    isLoggedIn, isAux, selectedCampaign, setSelectedCampaign,
    campaigns, auxReasons, seconds, handleLogout,
    handleAuxAction, data,
  } = useAgentContext() as any;

  const { mode } = useThemeMode();
  const isDark = mode === 'dark' || document.documentElement.getAttribute('data-bs-theme') === 'dark';

  const [telephonyMode, setTelephonyMode] = useState<'desktop' | 'web'>(() => {
    return (localStorage.getItem('telephony-device-mode') as 'desktop' | 'web') || 'desktop';
  });

  useEffect(() => {
    const modeHandler = (e: any) => {
      if (e?.detail) setTelephonyMode(e.detail);
    };
    window.addEventListener('telephony-device-mode-changed', modeHandler);
    return () => window.removeEventListener('telephony-device-mode-changed', modeHandler);
  }, []);

  const handleSetTelephonyMode = (newMode: 'desktop' | 'web') => {
    setTelephonyMode(newMode);
    localStorage.setItem('telephony-device-mode', newMode);
    window.dispatchEvent(new CustomEvent('telephony-device-mode-changed', { detail: newMode }));
  };

  const formatTime = (s: number) => new Date(s * 1000).toISOString().substr(11, 8);
  const queueLen = data?.queue?.length || 0;

  return (
    <>
      {/* ── 1. SINGLE-ROW MOBILE BOTTOM BAR (Dock Style like soaldanjawaban.com) ── */}
      <div
        className="MobileAgentBar-wrapper d-flex d-xl-none position-fixed bottom-0 start-0 w-100 px-3 align-items-center justify-content-between position-relative"
        style={{
          height: '56px',
          zIndex: 1030,
          background: isDark
            ? 'linear-gradient(180deg, rgba(30,41,59,0.98) 0%, rgba(15,23,42,0.98) 100%)'
            : 'linear-gradient(180deg, #1e3a8a 0%, #172554 100%)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(255, 255, 255, 0.15)',
          boxShadow: '0 -4px 30px rgba(0, 0, 0, 0.4)',
        }}
      >
        {/* LEFT SECTION (Menu + Wifi Status) */}
        <div className="d-flex align-items-center flex-shrink-0" style={{ gap: '8px' }}>
          {/* Burger Menu Button */}
          <button
            className="btn d-flex align-items-center gap-1.5 px-2.5 py-2 rounded-3 text-white fw-bold"
            style={{
              background: isOpen ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.20)',
              fontSize: '12px',
              height: '38px',
            }}
            onClick={() => setIsOpen(!isOpen)}
            title="Buka Menu"
          >
            <Menu size={16} className="me-1" />
            <span>Menu</span>
            {queueLen > 0 && (
              <span className="badge bg-danger rounded-pill px-1.5 py-0.5 fs-9 ms-1">{queueLen}</span>
            )}
          </button>

          {/* Softphone Status Only (Wifi Icon) */}
          <NavbarSoftphone dropUp={true} forceDark={true} variant="status-only" />
        </div>

        {/* DEAD CENTER: 3D Elevated Floating Home FAB (Efek Timbul) */}
        <div className="position-absolute start-50 translate-middle-x" style={{ top: '-22px', zIndex: 1040 }}>
          {/* Outer Cradle Notch Aura */}
          <div 
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{
              padding: '3px',
              background: isDark
                ? 'linear-gradient(180deg, rgba(59,130,246,0.35) 0%, rgba(15,23,42,0.85) 100%)'
                : 'linear-gradient(180deg, rgba(255,255,255,0.4) 0%, rgba(30,58,138,0.2) 100%)',
              boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.35)',
              borderRadius: '50%',
            }}
          >
            <Link
              to='/dashboard'
              className='d-flex align-items-center justify-content-center rounded-circle transition-all hover-elevate-up'
              style={{
                width: '54px',
                height: '54px',
                background: isDark
                  ? 'linear-gradient(145deg, #3b82f6 0%, #1d4ed8 45%, #0f172a 100%)'
                  : 'linear-gradient(145deg, #ffffff 0%, #f8fafc 40%, #cbd5e1 100%)',
                border: isDark ? '3px solid rgba(255, 255, 255, 0.5)' : '3.5px solid #ffffff',
                boxShadow: `
                  0 8px 20px rgba(0, 0, 0, 0.45),
                  0 -3px 12px rgba(59, 130, 246, 0.5),
                  inset 0 3px 4px rgba(255, 255, 255, 0.95),
                  inset 0 -3px 5px rgba(0, 0, 0, 0.28)
                `,
                cursor: 'pointer',
              }}
              title='Ke Beranda / Dashboard'
            >
              <img
                alt='Beranda'
                src={toAbsoluteUrl('/media/logos/favicon.png')}
                style={{ 
                  width: '30px', 
                  height: '30px', 
                  objectFit: 'contain', 
                  filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.35))' 
                }}
              />
            </Link>
          </div>
        </div>

        {/* RIGHT SECTION (Keypad Dialer + Status Aux) */}
        <div className="d-flex align-items-center flex-shrink-0" style={{ gap: '8px' }}>
          {/* Softphone Dialer Only */}
          <NavbarSoftphone dropUp={true} forceDark={true} variant="dialer-only" />

          {/* Quick Aux / Available Status Button */}
          <button
            className="btn d-flex align-items-center gap-1.5 px-2.5 rounded-3 fw-bold"
            style={{
              height: '38px',
              fontSize: '11px',
              background: isAux ? 'rgba(245,158,11,0.25)' : 'rgba(16,185,129,0.25)',
              border: isAux ? '1px solid rgba(252,211,77,0.5)' : '1px solid rgba(110,231,183,0.5)',
              color: '#ffffff',
            }}
            onClick={() => setIsOpen(true)}
            title="Klik untuk ubah status aux/available"
          >
            {isAux ? <Coffee size={13} className="text-warning me-1" /> : <Zap size={13} className="text-success me-1" />}
            <span className="text-truncate" style={{ maxWidth: '65px' }}>
              {isAux ? 'Break' : 'Active'}
            </span>
          </button>
        </div>
      </div>

      {/* ── 2. FULL-WIDTH DROPUP BOTTOM SHEET MODAL ── */}
      {isOpen && (
        <>
          {/* Dark Backdrop Overlay */}
          <div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{
              zIndex: 1055,
              background: 'rgba(0, 0, 0, 0.65)',
              backdropFilter: 'blur(5px)',
              WebkitBackdropFilter: 'blur(5px)',
            }}
            onClick={() => setIsOpen(false)}
          />

          {/* Bottom Sheet Drawer */}
          <div
            className="position-fixed bottom-0 start-0 w-100 rounded-top-5 p-4 animate__animated animate__fadeInUp animate__faster shadow-lg"
            style={{
              zIndex: 1060,
              background: isDark ? '#1e1e2d' : '#ffffff',
              borderTop: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.10)',
              boxShadow: '0 -16px 48px rgba(0,0,0,0.35)',
              maxHeight: '85vh',
              overflowY: 'auto',
              borderTopLeftRadius: '24px',
              borderTopRightRadius: '24px',
            }}
          >
            {/* Grab Handle Bar */}
            <div
              className="mx-auto mb-3 rounded-pill"
              style={{ width: '45px', height: '4px', background: isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.15)' }}
            />

            {/* Header: Title & Close */}
            <div className="d-flex justify-content-between align-items-center pb-3 mb-3 border-bottom border-secondary border-opacity-10">
              <div>
                <h5 className="mb-0 fw-boldest fs-6 text-body">Menu Agen & Telephony</h5>
                <span className="text-muted fs-8">Pengaturan status, campaign, dan perangkat softphone</span>
              </div>
              <button
                className="btn btn-icon btn-sm btn-light rounded-circle"
                onClick={() => setIsOpen(false)}
              >
                <X size={16} />
              </button>
            </div>

            {/* ── CARD 1: SHIFT STATUS & CAMPAIGN SELECTOR ── */}
            <div
              className="p-3 rounded-4 mb-3 d-flex align-items-center justify-content-between gap-2"
              style={{
                background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(241,245,249,0.8)',
                border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(226,232,240,0.9)',
              }}
            >
              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <div className="text-muted fs-9 text-uppercase fw-boldest mb-1">Campaign Aktif</div>
                <select
                  className="form-select form-select-sm fw-bold border-0"
                  style={{
                    background: isDark ? '#1e293b' : '#ffffff',
                    color: isDark ? '#f8fafc' : '#0f172a',
                    borderRadius: '8px',
                    fontSize: '12px',
                    padding: '6px 28px 6px 10px',
                    boxShadow: isDark ? '0 0 0 1px rgba(255,255,255,0.1)' : '0 1px 3px rgba(0,0,0,0.08)',
                    cursor: 'pointer',
                  }}
                  value={selectedCampaign || 'Select Campaign'}
                  onChange={(e) => setSelectedCampaign(e.target.value)}
                >
                  <option value="Select Campaign" disabled>
                    Pilih Campaign...
                  </option>
                  {campaigns && campaigns.length > 0 ? (
                    campaigns.map((c: any, i: number) => {
                      const cName = c.name || c.Name || c;
                      return (
                        <option key={c.id || i} value={cName}>
                          {cName}
                        </option>
                      );
                    })
                  ) : (
                    <option value="Default Campaign">Default Campaign</option>
                  )}
                </select>
              </div>

              {/* Shift Timer */}
              <div className="d-flex flex-column align-items-end flex-shrink-0">
                <span className="text-muted fs-9 text-uppercase fw-boldest mb-1">Durasi Shift</span>
                <div className="d-flex align-items-center gap-1 px-2.5 py-1.5 rounded-3 bg-body shadow-xs border border-secondary border-opacity-10">
                  <Timer size={12} className="text-primary" />
                  <span className="fw-bold font-monospace fs-8 text-body">{formatTime(seconds)}</span>
                </div>
              </div>
            </div>

            {/* ── CARD 2: TELEPHONY MODE TOGGLE (FULL-WIDTH TILES) ── */}
            <div className="mb-3">
              <label className="fw-boldest text-uppercase text-muted fs-9 mb-2 d-block letter-spacing-1">
                Mode Softphone Telephony
              </label>
              <div className="row g-2">
                {/* External Softphone */}
                <div className="col-6">
                  <button
                    className={clsx(
                      "btn w-100 p-3 rounded-3 d-flex flex-column align-items-center text-center gap-1 transition-all",
                      telephonyMode === 'desktop' ? "border-2 border-primary shadow-sm" : "border border-secondary border-opacity-10"
                    )}
                    style={{
                      background: telephonyMode === 'desktop'
                        ? (isDark ? 'rgba(139,92,246,0.20)' : 'rgba(139,92,246,0.10)')
                        : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(248,250,252,0.8)'),
                      color: telephonyMode === 'desktop' ? (isDark ? '#c4b5fd' : '#7c3aed') : 'var(--bs-body-color)',
                    }}
                    onClick={() => handleSetTelephonyMode('desktop')}
                  >
                    <Monitor size={18} />
                    <span className="fw-bold fs-7 mt-1">External Softphone</span>
                    <span className="fs-9 opacity-75">SIP / Deskphone</span>
                  </button>
                </div>

                {/* Internal Softphone */}
                <div className="col-6">
                  <button
                    className={clsx(
                      "btn w-100 p-3 rounded-3 d-flex flex-column align-items-center text-center gap-1 transition-all",
                      telephonyMode === 'web' ? "border-2 border-primary shadow-sm" : "border border-secondary border-opacity-10"
                    )}
                    style={{
                      background: telephonyMode === 'web'
                        ? (isDark ? 'rgba(59,130,246,0.20)' : 'rgba(59,130,246,0.10)')
                        : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(248,250,252,0.8)'),
                      color: telephonyMode === 'web' ? (isDark ? '#93c5fd' : '#2563eb') : 'var(--bs-body-color)',
                    }}
                    onClick={() => handleSetTelephonyMode('web')}
                  >
                    <Globe size={18} />
                    <span className="fw-bold fs-7 mt-1">Internal Softphone</span>
                    <span className="fs-9 opacity-75">WebRTC Browser</span>
                  </button>
                </div>
              </div>
            </div>

            {/* ── CARD 3: ANTREAN / QUEUE DROPDOWN ── */}
            <div className="mb-3">
              <button
                className="btn w-100 p-3 rounded-3 d-flex align-items-center justify-content-between border border-secondary border-opacity-10"
                style={{
                  background: queueLen > 0
                    ? (isDark ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.08)')
                    : (isDark ? 'rgba(255,255,255,0.03)' : 'rgba(248,250,252,0.8)'),
                }}
                onClick={() => setShowQueueDetails(!showQueueDetails)}
              >
                <div className="text-start">
                  <div className="fw-bold fs-7 text-body">Panggilan dalam Antrean</div>
                  <div className="text-muted fs-9">{queueLen > 0 ? `${queueLen} nasabah menunggu` : 'Tidak ada antrean'}</div>
                </div>
                <span className={clsx("badge rounded-pill px-2.5 py-1 fw-bold fs-8", queueLen > 0 ? "bg-danger text-white" : "bg-light text-muted")}>
                  {queueLen} Waiting
                </span>
              </button>

              {/* Queue Expanded Details */}
              {showQueueDetails && queueLen > 0 && (
                <div className="mt-2 p-2 rounded-3 border border-danger border-opacity-25 bg-danger bg-opacity-5">
                  {data.queue.map((d: any, idx: number) => (
                    <div key={idx} className="d-flex align-items-center justify-content-between p-2 border-bottom border-danger border-opacity-10 last:border-0">
                      <div className="d-flex align-items-center gap-2">
                        <PhoneCall size={14} className="text-danger" />
                        <span className="fw-bold fs-7 font-monospace">{d.phone}</span>
                      </div>
                      <div className="d-flex align-items-center gap-1 text-muted fs-9">
                        <Clock size={11} /> <span className="text-danger fw-bold">{d.waitTime}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── CARD 4: AUX / STATUS BREAK SELECTION (FULL-WIDTH GRID) ── */}
            <div className="mb-4">
              <label className="fw-boldest text-uppercase text-muted fs-9 mb-2 d-block letter-spacing-1">
                Pilih Status Agen
              </label>

              {/* Available Tile */}
              <button
                className={clsx(
                  "btn w-100 p-3 rounded-3 d-flex align-items-center justify-content-between mb-2 transition-all",
                  !isAux ? "btn-success text-white shadow-sm" : "btn-light border border-secondary border-opacity-10 text-body"
                )}
                onClick={() => { handleAuxAction(null); setIsOpen(false); }}
              >
                <div className="text-start">
                  <div className="fw-boldest fs-7">AVAILABLE (SIAP TERIMA PANGGILAN)</div>
                  <div className="fs-9 opacity-85">Aktif menerima antrean & panggilan keluar</div>
                </div>
                {!isAux && <span className="badge bg-white text-success fw-boldest fs-9">AKTIF</span>}
              </button>

              {/* Break Reasons Grid */}
              <div className="row g-2">
                {auxReasons && auxReasons.length > 0 ? (
                  auxReasons.map((reason: any) => (
                    <div className="col-6" key={reason.id}>
                      <button
                        className="btn btn-light w-100 p-2.5 rounded-3 d-flex align-items-center justify-content-center text-center border border-secondary border-opacity-10"
                        style={{
                          background: isAux && reason.reasonName ? 'rgba(245,158,11,0.12)' : undefined,
                          borderColor: isAux && reason.reasonName ? 'rgba(245,158,11,0.40)' : undefined,
                        }}
                        onClick={() => { handleAuxAction(reason.id); setIsOpen(false); }}
                      >
                        <span className="fw-semibold fs-8 text-truncate">{reason.reasonName}</span>
                      </button>
                    </div>
                  ))
                ) : (
                  ['Istirahat', 'Toilet', 'Sholat', 'Briefing'].map((r, i) => (
                    <div className="col-6" key={i}>
                      <button
                        className="btn btn-light w-100 p-2.5 rounded-3 d-flex align-items-center justify-content-center text-center border border-secondary border-opacity-10"
                        onClick={() => { handleAuxAction(i + 1); setIsOpen(false); }}
                      >
                        <span className="fw-semibold fs-8 text-truncate">{r}</span>
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* ── CARD 5: LOGOUT SHIFT ── */}
            <button
              className="btn btn-outline-danger w-100 py-3 rounded-3 d-flex align-items-center justify-content-center gap-2 fw-bold fs-7"
              onClick={() => { handleLogout(); setIsOpen(false); }}
            >
              <LogOut size={15} />
              <span>Akhiri Shift / Logout</span>
            </button>
          </div>
        </>
      )}
    </>
  );
};
